import React, { useCallback, useContext, useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

import { identityText, pagination, reset_password } from "../../../locales/en";
import OnboardingIdentityInfoModal from "../../../components/modals/OnboardingIdentityInfoModal";
import { IsCreditConsentAccepted } from "./UseAdditionalComponent";
import MultiStepFormContext from "../../../components/MultiStepForm/MultiStepFormContext";
import {
  getAcceptCreditConsentTokenUrl,
  getAplayIdRedirectWebTokenURL
} from "../../../utils/constants/apiEndPoints";
import { useNavigate, useSearchParams } from "react-router-dom";
import OnboardingIdentityTokenModal from "../../../components/modals/OnboardingIdentityTokenModal";
import publicAxios from "../../../utils/publicAxios";
import { FaCheckCircle } from "react-icons/fa";
import { NOT_FOUND, VERIFY_YOUR_IDENTITY } from "../../../route/paths";
import { fetchApplyIdStatusThunk } from "../../../redux/agreementPagesSlice";
import { useDispatch } from "react-redux";
import CircleLoader from "../../../components/common/CircleLoader";

const Identity = () => {
  const { next } = useContext(MultiStepFormContext);
  const [searchParams] = useSearchParams();
  const [getToken, setToken] = useState("");
  const [mobileLink, setMobileLink] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState();
  const [showMessage, setShowMessage] = useState();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrUrl, setQrUrl] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchApplyIdStatus = useCallback(async () => {
    try {
      setLoading(true);
      const payload = {
        token: getToken
      };
      const response = await dispatch(
        fetchApplyIdStatusThunk(payload)
      ).unwrap();
      setStatus(response.data?.data?.status);
      setSuccessMsg(response.data?.message);
    } catch {
      navigate(NOT_FOUND);
    } finally {
      setLoading(false);
    }
  }, [getToken, dispatch, navigate]);

  const handleQrScanFunction = useCallback(async () => {
    try {
      const response = await publicAxios.post(
        getAplayIdRedirectWebTokenURL,
        {},
        {
          params: {
            token: getToken
          }
        }
      );
      const result = response?.data?.data;
      if (result?.status) {
        const redirectUrl = result?.start_process_url;
        setQrUrl(redirectUrl);
      }
    } catch {
      navigate(NOT_FOUND);
    }
  }, [getToken]);

  const handleSubmit = () => {
    next();
  };

  const [isCreditConsentAccepted, setIsCreditConsentAccepted] = useState(false);

  const handleOnchange = async () => {
    setIsCreditConsentAccepted(true);
    try {
      await publicAxios.post(
        getAcceptCreditConsentTokenUrl,
        {},
        {
          params: {
            acceptConsent: true,
            token: getToken
          }
        }
      );
    } catch {
      setIsCreditConsentAccepted(false);
    }
  };

  const handleOpenSendLink = () => {
    setMobileLink(!mobileLink);
  };

  const generateRandomId = () => {
    const userPrefix = "user";
    const randomNumber = Math.floor(1000 + Math.random() * 9000);
    const timestamp = Date.now();
    return `${userPrefix}_${randomNumber}-${timestamp}`;
  };

  const handleCompleteOnMobile = () => {
    const id = generateRandomId();
    window.open(VERIFY_YOUR_IDENTITY(id), "_blank");
  };

  const fetchStatus = useCallback(() => {
    if (getToken) {
      fetchApplyIdStatus();
    }
  }, [getToken, fetchApplyIdStatus]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 20000);

    return () => clearInterval(interval);
  }, [fetchStatus]);

  useEffect(() => {
    if (getToken) {
      handleQrScanFunction();
      handleOnchange(getToken);
    }
  }, [handleQrScanFunction, getToken]);

  const handleInfoModal = () => {
    setInfoModal(!infoModal);
  };

  useEffect(() => {
    const token = searchParams.get("token");
    setToken(token);
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowMessage(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <CircleLoader />;
  }

  return (
    <>
      <div className="container">
        <div className="row">
          <div className="col-lg-8 offset-lg-2">
            <div className="step-wrap">
              <div className="step-title">
                <h3>{identityText.step1title}</h3>
              </div>

              <div className="step-content">
                {status ? (
                  <div className="success-message">
                    {successMsg && (
                      <p>
                        <FaCheckCircle /> {successMsg}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="aplyid-section">
                    <IsCreditConsentAccepted
                      handleInfoModal={handleInfoModal}
                      isChecked={isCreditConsentAccepted}
                    />
                    <div className="desktop-content-box-wrap d-flex flex-wrap position-relative mt-4">
                      <div className="left-section">
                        <div className="desktop-content-blog d-flex flex-wrap">
                          <div className="desktop-device-wrap">
                            <h4 className="text-center">
                              {identityText.completeOnMobile}
                            </h4>
                            <p>{identityText.ifYou}</p>
                          </div>
                          <div className="continue-mobile-btn-wrap d-flex flex-wrap justify-content-center mt-2 mb-2">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleCompleteOnMobile}
                            >
                              {identityText.completeOnMobile}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="separet-or">
                        <p>{reset_password?.or}</p>
                      </div>
                      <div className="right-section">
                        <div className="desktop-content-blog">
                          <h4 className="text-center">
                            {identityText.completeOnDesktop}
                          </h4>
                          <p>{identityText.ifUsing}</p>
                          <div className="aplyid-qr-code-wrapper">
                            <div className="canvas-wrap">
                              <QRCodeCanvas
                                value={qrUrl}
                                size={140}
                                bgColor="#ffffff"
                                fgColor="#000000"
                                level="H"
                              />
                            </div>
                          </div>
                          <div className="continue-mobile-btn-wrap d-flex flex-wrap justify-content-center mt-2 mb-2">
                            <button
                              className="btn btn-primary"
                              onClick={handleOpenSendLink}
                            >
                              {identityText.sendLink}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {status !== true &&
                      status !== undefined &&
                      status !== null &&
                      showMessage && (
                        <p className="small mt-3">{identityText.onceYou}</p>
                      )}
                    <div className="mb-5" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="step-btn-group d-flex justify-content-center align-items-center mt-5 mb-5 flex-wrap">
        <div className="next-btn p-1">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!status}
          >
            {pagination.saveContinue}
          </button>
        </div>
      </div>

      <OnboardingIdentityTokenModal
        isOpen={mobileLink}
        getToken={getToken}
        toggle={handleOpenSendLink}
      />

      <OnboardingIdentityInfoModal
        isOpen={infoModal}
        toggle={handleInfoModal}
      />
    </>
  );
};

export default Identity;
